import LRU from 'lru-cache';
import * as mediasoupClient from 'mediasoup-client';
import { post } from '../utils/api';
import SocketTransport from './SocketTransport';
import logger from '../utils/logger';
import {
  SOCKET_INIT,
  roomId,
  streamTimeout,
  testTimeOut,
  statsIterationTimer,
} from '../utils/constants';
import StreamHandler from './StreamHandler';
import { getToken } from '../utils/token';
import { sleep } from '../utils/sleep';

const BITRATE_0 = 80000;
const BITRATE_1 = 300000;
const BITRATE_2 = 800000;

const iceServersCache = new LRU({
  ttl: 1000 * 60,
  max: 1,
});

// Helper function to wait for stream to start
function waitForStreamStart(streamHandler, timeout) {
  return new Promise(async (resolve, reject) => {
    const interval = 200; // Time in milliseconds between checks
    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      if (streamHandler._videoStream || streamHandler._audioStream) {
        resolve();
        return;
      }

      await sleep(interval);
    }

    reject(
      new Error('Timeout: Stream did not start within the specified time.')
    );
  });
}

// Helper function to wait for connection state change
function waitForConnectionStateChange(streamProducerTransport, timeout) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      streamProducerTransport.off('connectionstatechange', eventListener);
      resolve(false);
    }, timeout);

    const eventListener = (state) => {
      if (state === 'connected') {
        clearTimeout(timer);
        streamProducerTransport.off('connectionstatechange', eventListener);
        resolve(true);
      }
    };

    streamProducerTransport.on('connectionstatechange', eventListener);
  });
}

export class RoomClient {
  constructor() {
    this._closed = false;
    this._token = null;
    this._socket = null;
    this._device = null;
    this._producerTransport = null;
    this._audioProducer = null;
    this._videoProducer = null;
    this.streamHandler = new StreamHandler();
  }

  close() {
    if (this._closed) return;
    this._closed = true;
    this._socket?.close();
    this._token = null;
    this._producerTransport?.close();
    this._producerTransport?.removeAllListeners();
    this._audioProducer?.close();
    this._audioProducer = null;
    this._videoProducer?.close();
    this._videoProducer = null;
    this._producerTransport = null;
    this._device = null;
    this._socket = null;
    this.streamHandler.close();
  }

  async test() {
    const token = await getToken();
    if (!token) {
      throw new Error('tokenError');
    }
    try {
      await this.initSocket(token);
    } catch (e) {
      throw new Error('socketError');
    }
    const roomCreated = await this.createRoom(roomId);
    if (!roomCreated) throw new Error('unexpectedError');
    const joinedRes = await this.joinRoom();
    if (!joinedRes.ok) throw new Error('unexpectedError');
    const iceServers = await this.getIceServers();
    if (iceServers.length === 0) throw new Error('unexpectedError');
    await this.setupDevice();
    if (!this._device) throw new Error('socketError');
    try {
      await this.setupProducerTransport();
    } catch (e) {
      throw new Error('unexpectedError');
    }
    const connectionState = waitForConnectionStateChange(
      this._producerTransport,
      streamTimeout
    );

    // check if stream is started
    try {
      await waitForStreamStart(this.streamHandler, streamTimeout);
    } catch {
      throw new Error('streamError');
    }

    let streamProducer;
    if (this.streamHandler._videoStream) {
      streamProducer = await this.startVideoProducer(
        this.streamHandler._videoStream
      );
    } else {
      streamProducer = await this.startAudioProducer(
        this.streamHandler._audioStream
      );
    }
    if (!streamProducer) throw new Error('streamError');
    const connectionResult = await connectionState;
    if (!connectionResult) throw new Error('streamError');

    let timeConsumed = 0;
    let serverStats, transportStats;

    while (timeConsumed < testTimeOut) {
      const stats = await streamProducer.getStats();
      transportStats = Array.from(stats.values()).filter(
        (report) => report.type === 'candidate-pair' && report.bytesSent
      );

      serverStats = await this.requestStats(streamProducer.id);
      logger.log('Server stats', Array.from(serverStats));
      timeConsumed = timeConsumed + statsIterationTimer;
      await sleep(statsIterationTimer);
    }

    if (transportStats[0].bytesSent <= 0) {
      throw new Error('streamingError');
    }
    const roundTripTime = serverStats.stats[0]?.roundTripTime;
    if (!roundTripTime) throw new Error('streamingError');
    if (roundTripTime < 700) {
      return { status: 'good' };
    }
    if (roundTripTime < 1000) {
      return { status: 'warning' };
    }
    return { status: 'bad' };
  }

  async request(event, data) {
    if (!this._socket) return;
    if (this._closed) {
      throw new Error("Can't request as RoomClient already closed!");
    }
    const response = await this._socket.request(event, data);
    return response;
  }

  _gotMessageFromServer = async (message) => {
    logger.log('Got message from server', message);
  };

  async initSocket(token) {
    let state = SOCKET_INIT;
    this._token = token;
    logger.log('Going to initialize Socket Transport!', state);
    this._socket = new SocketTransport(token, state);

    this._socket.on('message', (message) =>
      this._gotMessageFromServer(message)
    );

    const connectionStatus = await this._socket.connect();

    this._socket.on('connected', () => {
      logger.log('Socket connected in Init socket');
    });
    return connectionStatus;
  }

  async createRoom() {
    try {
      await post(`/room/${roomId}`, {}, this._token);
      return true;
    } catch (err) {
      const { status, data } = err?.response || {};
      if (status === 409 && data?.message === 'Room already exists')
        return true;
      return false;
    }
  }

  joinRoom() {
    return new Promise((resolve, reject) => {
      this.request('joinRoom', this._roomId).then((joinedRes) => {
        logger.log('JOIN ROOM RES', joinedRes);

        if (!joinedRes.ok) {
          reject(joinedRes); // Reject the Promise in case of an error
        }

        resolve(joinedRes); // Resolve the Promise with joinedRes
      });
    });
  }

  async getIceServers() {
    if (iceServersCache.has('iceServers')) {
      return iceServersCache.get('iceServers');
    }

    if (!window.__SHOULD_FETCH_ICE_SERVERS__) {
      return [
        {
          urls: `turn:${window.__TURN_ADDR__}:${window.__TURN_PORT__}?transport=udp`,
          username: window.__TURN_USER__,
          credential: window.__TURN_PASS__,
        },
      ];
    }

    let iceServers = await this._socket.request('getIceServers');

    iceServers = iceServers.map((server) => ({
      urls: server.url,
      ...server,
    }));

    delete iceServers.url;

    iceServersCache.set('iceServers', iceServers);

    return iceServers;
  }

  async setupDevice() {
    const routerRtpCapabilities = await this._socket.request(
      'getRouterRtpCapabilities',
      {}
    );
    if (!routerRtpCapabilities) return null;

    const device = new mediasoupClient.Device({
      iceServers: await this.getIceServers(),
    });
    await device.load({ routerRtpCapabilities });
    this._device = device;
  }

  async setupProducerTransport() {
    const producerData = await this._socket.request('createProducerTransport', {
      rtpCapabilities: this._device.rtpCapabilities,
    });

    logger.log('producerData', producerData);

    const transport = window.__ENABLE_TURN__
      ? this._device.createSendTransport({
          ...producerData,
          iceServers: await this.getIceServers(),
          iceTransportPolicy: 'relay',
        })
      : this._device.createSendTransport(producerData);

    transport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      logger.log('producer transport connect');
      this._socket
        .request('connectProducerTransport', {
          transportId: transport.id,
          dtlsParameters,
        })
        .then(callback)
        .catch(errback);
    });

    transport.on(
      'produce',
      async ({ kind, rtpParameters, appData }, callback, errback) => {
        logger.log('producer transport produce');
        const { id } = await this._socket.request('produce', {
          transportId: transport.id,
          kind,
          rtpParameters,
          appData,
        });
        callback({ id });
      }
    );

    transport.on('connectionstatechange', (state) => {
      logger.log('produce state changed', state);
    });
    this._producerTransport = transport;
  }

  async startVideoProducer(stream) {
    const videotracks = stream.getVideoTracks();
    if (!videotracks.length) return {};
    const videoTrack = videotracks[0];
    const producer = await this.startProducer({ videoTrack });
    this._videoProducer = producer.videoProducer;

    return producer.videoProducer;
  }

  async startAudioProducer(stream) {
    const audiotracks = stream.getAudioTracks();
    if (!audiotracks.length) return {};
    const audioTrack = audiotracks[0];
    const producer = await this.startProducer({ audioTrack });
    this._audioProducer = producer.audioProducer;

    return producer.audioProducer;
  }

  async startProducer({ videoTrack, audioTrack }) {
    let videoProducer;
    let audioProducer;
    const ps = [];

    const firstVideoCodec = this._device.rtpCapabilities.codecs.find(
      (c) => c.kind === 'video'
    );

    const isAndroid = /(android)/i.test(navigator.userAgent);
    const vp9 = firstVideoCodec.mimeType.toLowerCase().includes('vp9');
    if (vp9) {
      logger.log('using vp9');
    }

    // Chromium on Android has an issue when combining captureStream and vp8
    //  We cannot use scaleResolutionDownBy on android
    //  See https://bugs.chromium.org/p/chromium/issues/detail?id=1359707
    if (videoTrack) {
      logger.log('send video produce');
      const p = this._producerTransport
        .produce({
          track: videoTrack,
          encodings: vp9
            ? [{ scalabilityMode: 'L3T3_KEY', maxBitrate: BITRATE_0 }]
            : isAndroid
            ? [
                { maxBitrate: BITRATE_0, scalabilityMode: 'L1T2' },
                { maxBitrate: BITRATE_1, scalabilityMode: 'L1T2' },
                { maxBitrate: BITRATE_2, scalabilityMode: 'L1T2' },
              ]
            : [
                {
                  scaleResolutionDownBy: 4,
                  maxBitrate: BITRATE_0,
                  scalabilityMode: 'L1T2',
                },
                {
                  scaleResolutionDownBy: 2,
                  maxBitrate: BITRATE_1,
                  scalabilityMode: 'L1T2',
                },
                {
                  scaleResolutionDownBy: 1,
                  maxBitrate: BITRATE_2,
                  scalabilityMode: 'L1T2',
                },
              ],
          codecOptions: {
            videoGoogleStartBitrate: 1000,
          },
          disableTrackOnPause: false,
          //zeroRtpOnPause: true, // REMOVE ME only for debugging data transfers
        })
        .then((producer) => {
          videoProducer = producer;
          videoProducer.pause();
        });
      ps.push(p);
    }

    this._videoProducer = videoProducer;

    // AUDIO
    if (audioTrack) {
      logger.log('send audio produce');
      const p = this._producerTransport
        .produce({
          track: audioTrack,
          disableTrackOnPause: true,
          codecOptions: {
            opusStereo: 0,
            opusDtx: 0, // TODO we want this to be 1, but for recording it needs to be 0, otherwise ffmpeg shrinks audio on silence
          },
        })
        .then((producer) => {
          audioProducer = producer;
          logger.log('audio produce done', audioProducer.id);
        });
      ps.push(p);
    }

    this._audioProducer = audioProducer;

    await Promise.all(ps);

    return { videoProducer, audioProducer };
  }

  async requestStats(producerId) {
    const stats = await this.request('producerStats', { id: producerId });
    return stats;
  }

  async replaceStreamTrack({ stream, type }) {
    if (!this._producerTransport) {
      // producer transport not ready
      return;
    }
    if (!this._videoProducer && !this._audioProducer) {
      // not producing any stream
      return;
    }
    if (type === 'video' && !this._videoProducer) {
      // not producing video stream
      return;
    }
    if (type === 'audio' && !this._audioProducer) {
      // not producing audio stream
      return;
    }
    let track;
    try {
      if (type === 'video') {
        track = stream.getVideoTracks()[0];
      } else {
        track = stream.getAudioTracks()[0];
      }
    } catch {
      return;
    }
    if (type === 'video') {
      this._videoProducer.replaceTrack({ track });
    } else {
      this._audioProducer.replaceTrack({ track });
    }
  }
}
